import { memo, useRef } from "react"
import type { BaseSyntheticEvent, FC, NamedExoticComponent, ReactElement, RefObject } from "react"

import { motion, useReducedMotion } from "framer-motion"
import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import isNull from "lodash-es/isNull"
import keys from "lodash-es/keys"
import map from "lodash-es/map"
import reject from "lodash-es/reject"
import values from "lodash-es/values"
import { useUpdate } from "react-use"

import { buildCssClassName, propsAreEqual, withPressHandler } from "@/3514/utils"
import { type TEmptyCallback } from "@/shared/types/functions"

import { ParticipantHold as Hold } from "./participant.hold.component"
import { participantHoldStyles as styles } from "./participant.hold.styles"
import {
    EParticipantHoldsMode as EMode,
    type TParticipantHoldsProps as TProps,
    TParticipantHoldSession as TSession
} from "./participant.hold.types"

const { container: classes } = styles

const ParticipantHolds: FC<TProps> = ({
    className = String(""),
    onHoldSelect = undefined,
    mode,
    firstSessionGetter,
    restSessionsGetter,
    sessionsSet
}: TProps): ReactElement => {
    const isSelectable: boolean = eq(mode, EMode.Selectable)

    const isMotionReduced: boolean = useReducedMotion()

    const keysSet: string[] = keys(sessionsSet)

    const selectedIds: RefObject<string[]> = useRef<string[]>([])

    const triggerUpdate: TEmptyCallback = useUpdate()

    function onSelectAction(e: BaseSyntheticEvent): void {
        const elem: HTMLElement = e.target.closest("[data-index]")

        if (isNull(elem)) return void 0

        const elemIndexValue: string = elem.getAttribute("data-index")

        if (includes(selectedIds.current, elemIndexValue))
            selectedIds.current = reject(selectedIds.current, (i: string): boolean => i === elemIndexValue)
        else selectedIds.current.push(elemIndexValue)

        triggerUpdate()

        onHoldSelect?.(selectedIds.current)

        return void 0
    }

    return (
        <motion.div
            variants={{ hidden: {}, visible: { transition: { staggerChildren: isMotionReduced ? 0 : 0.15 } } }}
            initial="hidden"
            animate="visible"
            {...(isSelectable
                ? { onClick: withPressHandler(onSelectAction), onKeyDown: withPressHandler(onSelectAction) }
                : {})}
            className={buildCssClassName(classes.container(), { [className]: className })}
        >
            {map<TSession[], ReactElement>(values(sessionsSet), (sessions: TSession[], idx: number): ReactElement => {
                const key: string = keysSet[idx]

                return (
                    <motion.div
                        key={key}
                        data-index={key}
                        variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                        transition={{ duration: isMotionReduced ? 0 : 0.3, ease: "easeOut", type: "tween" }}
                        className={classes.childContainer()}
                    >
                        <Hold
                            mode={mode}
                            setIndex={idx}
                            isSelected={includes<string>(selectedIds.current, key)}
                            firstSessionGetter={firstSessionGetter}
                            restSessionsGetter={restSessionsGetter}
                            sessions={sessions}
                        />
                    </motion.div>
                )
            })}
        </motion.div>
    )
}

ParticipantHolds.displayName = "ParticipantHolds"

const MemoizedHolds: NamedExoticComponent<TProps> = memo<TProps>(ParticipantHolds, propsAreEqual)

export { MemoizedHolds as ParticipantHolds }
